import UUID from "../../Utilities/Generic/UUID";
import AgentManager from "../Bases/AgentManager";
import { SearchAgent } from "./SearchAgent";
import { type SearchIndexes } from "./SearchEvents";

export class SearchCommunicator {
    private static awaitingResponse = new Map<string, (response: any) => void>;
    private static broadcastChannel: BroadcastChannel | undefined;
    private static bulkBroadcast = {} as Record<string, {
        running: boolean;
        waiting: Array<unknown>;
    }>;

    private static getBroadcastChannel(): BroadcastChannel {
        if(this.broadcastChannel) {
            return this.broadcastChannel;
        }

        this.broadcastChannel = new BroadcastChannel("searchCommunicator");
        this.broadcastChannel.addEventListener("message", event => {
            if(typeof event.data !== "object" || event.data === null) {
                return;
            }

            if(typeof event.data.id !== "string") {
                return;
            }

            this.awaitingResponse.get(event.data.id)?.(event.data.response);
        });

        return this.broadcastChannel;
    }

    private static bulkSendThroughBroadcast<
        TMethod extends (keyof SearchAgent)
    >(
        method: TMethod,
        args: Parameters<SearchAgent[TMethod]>
    ): Promise<void> {
        if(typeof this.bulkBroadcast[method] === "undefined") {
            this.bulkBroadcast[method] = {
                running: false,
                waiting: [],
            };
        }

        this.bulkBroadcast[method].waiting.push(args);

        if(this.bulkBroadcast[method].running) {
            return Promise.resolve();
        }

        this.bulkBroadcast[method].running = true;
        setTimeout(() => {
            const toSend = this.bulkBroadcast[method].waiting;

            this.bulkBroadcast[method].waiting = [];
            this.bulkBroadcast[method].running = false;
            void this.sendThroughBroadcast("bulk", [ method, toSend ]);
        }, Math.floor(Math.random() * (2500 - 1200) + 1200));

        return Promise.resolve();
    }

    private static sendThroughBroadcast<
        TMethod extends (keyof SearchAgent),
        TReturn extends ReturnType<SearchAgent[TMethod]>
    >(
        method: TMethod,
        args: Parameters<SearchAgent[TMethod]>,
        supportsBulkMessages = false
    ): Promise<TReturn> {
        if(supportsBulkMessages) {
            // If we are supporting bulk messages, we are always going to respond with undefined
            return this.bulkSendThroughBroadcast(method, args) as Promise<TReturn>;
        }

        return new Promise((res, rej) => {
            let hasTimedOut = false;
            const timeout = setTimeout(() => {
                rej("Did not receive a response from search worker in time");
                hasTimedOut = true;
            }, 5000);

            const id = UUID.generate();
            const callback = (response: TReturn) => {
                SearchCommunicator.awaitingResponse.delete(id);

                if(hasTimedOut) {
                    return;
                }

                clearTimeout(timeout);

                res(response);
            };

            SearchCommunicator.awaitingResponse.set(id, callback);
            SearchCommunicator.getBroadcastChannel().postMessage({
                id,
                method,
                args,
            });
        });
    }

    private static sendMessage<TMethod extends (keyof SearchAgent)>(
        method: TMethod,
        args: Parameters<SearchAgent[TMethod]>,
        supportsBulkMessage = false
    ): Promise<Awaited<ReturnType<SearchAgent[TMethod]>>> {
        if(typeof window === "undefined") {
            // Send a message through a broadcast channel
            return SearchCommunicator.sendThroughBroadcast(method, args, supportsBulkMessage);
        } else {
            // Send the message through the agent
            return AgentManager.getAgent(SearchAgent)[method as "search"].apply(
                null,
                args as Parameters<SearchAgent["search"]>
            ) as any;
        }
    }

    public static emptyIndex(index: SearchIndexes): Promise<void> {
        return SearchCommunicator.sendMessage("emptyIndex", [ index ]);
    }

    public static search(index: SearchIndexes, needle: string): Promise<Array<string>> {
        return SearchCommunicator.sendMessage("search", [ index, needle ]);
    }

    public static updateIndex(
        index: SearchIndexes,
        id: string,
        values: string | Array<string> | undefined
    ): Promise<void> {
        return SearchCommunicator.sendMessage("updateIndex", [ index, id, values ], true);
    }
}
